import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { getWebSocketURL, sendSocketMessage } from "../socket";
import { getStreamingQuality, determineNearestRegion, isSessionValidAndActive } from "./utilsRS";
import { UpdateRoomInviteUserData } from "../api/RoomsController";
import { getFromSessionStorage } from "../hooks/useSessionStorage";

const FIRST_FRAME_TIMEOUT = 15000;
const MAX_AUTH_RETRIES = 3;
const AUTH_RETRY_BASE_DELAY_MS = 2000;

export const useDCVStreaming = (roomIdVanity, selectedDevices) => {
    const user = useSelector((state) => state.user);
    // Track elapsed seconds for loader progress during polling
    const [pollingElapsedSeconds, setPollingElapsedSeconds] = useState(0);
    const pollingTimerRef = useRef(null);
    const dcvDisplayRef = useRef(null);
    const [dcvLoaded, setDcvLoaded] = useState(false);
    const [streamingUrl, setStreamingUrl] = useState("");
    const firstFrameTimeoutRef = useRef(null);
    const [autoVacantEmitted, setAutoVacantEmitted] = useState(false);
    const [firstFrameReceived, setFirstFrameReceived] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    const [clientTag, setClientTag] = useState(JSON.parse(getFromSessionStorage("vanityLinkData"))?.clientTag || "");
    const [loaderText, setLoaderText] = useState("Starting...");

    // Auth error tracking & retry state
    const [authError, setAuthError] = useState(null);
    const [authRetryCount, setAuthRetryCount] = useState(0);
    const [isRetrying, setIsRetrying] = useState(false);
    const authRetryTimeoutRef = useRef(null);
    const handleAuthErrorRef = useRef(null); // Ref to hold auth error handler for useEffect access
    const isIntentionalDisconnectRef = useRef(false); // Track intentional disconnects (retry flow) to avoid redirect
    const activePollRef = useRef(null);
    const inviteSettings = localStorage.getItem("inviteSettings");
    const avatarPreset = sessionStorage.getItem("selectedAvatar") || "";
    const roomId = localStorage.getItem("inviteRoomId") || roomIdVanity;
    const dcvConnectionRef = useRef(null);
    const iframeReadyRef = useRef(false);

    const baseLaunchData = useMemo(() => {
        return {
            inviteSettings,
            avatarPreset,
            roomId,
            userId: user.userId,
            token: user.token,
            coreApiUri: process.env.REACT_APP_CORE_API_URI,
            environmentType: process.env.REACT_APP_STREAM_API_ENV,
            websocketUrl: getWebSocketURL(),
            home: process.env.REACT_APP_WEB_URL,
        };
    }, [user]);

    const iframeMessageQueueRef = useRef([]);

    const tryPostToIframeNow = useCallback((msg) => {
        const iframe = dcvDisplayRef.current;
        if (!iframe) {
            console.warn("tryPostToIframeNow: no iframe element yet");
            return false;
        }
        const win = iframe.contentWindow;
        if (!win) {
            console.warn("tryPostToIframeNow: iframe.contentWindow not available yet");
            return false;
        }

        const targetOrigin = window.location.origin;
        try {
            win.postMessage(msg, targetOrigin);
            console.log("postToIframe: posted message", msg.type || msg);
            return true;
        } catch (err) {
            console.warn("postToIframe: postMessage threw", err, msg);
            return false;
        }
    }, []);

    // returns user to loading/home on disconnect
    const handleDcvDisconnect = useCallback(() => {
        // localStorage.removeItem("dcvSessionData");
        if (baseLaunchData.home) window.location.href = baseLaunchData.home;
        // else window.location.reload();
        console.log("[useDCVStreaming] Handling DCV disconnect, redirecting to home.");
    }, [baseLaunchData.home]);

    const postToIframe = useCallback(
        (msg, { timeout = 10000 } = {}) => {
            console.log("postToIframe called", msg);
            if (tryPostToIframeNow(msg)) return Promise.resolve(true);

            return new Promise((resolve, reject) => {
                const entry = { msg, resolve, reject, deadline: Date.now() + timeout };
                iframeMessageQueueRef.current.push(entry);

                // simple poll to try posting periodically (stops automatically on resolve/reject)
                const pollIntervalMs = 200;
                const timer = setInterval(() => {
                    // if entry removed, stop
                    if (!iframeMessageQueueRef.current.includes(entry)) {
                        clearInterval(timer);
                        return;
                    }
                    if (tryPostToIframeNow(msg)) {
                        // flush this entry
                        iframeMessageQueueRef.current = iframeMessageQueueRef.current.filter((e) => e !== entry);
                        entry.resolve(true);
                        clearInterval(timer);
                        return;
                    }
                    if (Date.now() > entry.deadline) {
                        iframeMessageQueueRef.current = iframeMessageQueueRef.current.filter((e) => e !== entry);
                        entry.reject(new Error("postToIframe timeout waiting for iframe"));
                        console.error("postToIframe: timeout waiting for iframe window to become available");
                        clearInterval(timer);
                    }
                }, pollIntervalMs);
            });
        },
        [tryPostToIframeNow]
    );

    //  send a dcv:run command to iframe
    const postIframeRun = useCallback(
        (cmd, payload = {}) => {
            postToIframe({ type: "dcv:run", cmd, payload });
        },
        [postToIframe]
    );

    // send device settings to iframe
    const postSetDevices = useCallback(
        ({ audioInput, webcamInput, audioOutput }) => {
            // only send devices that are present/non-empty
            const payload = {
                type: "dcv:setDevices",
                devices: {
                    audioInput: audioInput || "",
                    webcamInput: webcamInput || "",
                    audioOutput: audioOutput || "",
                },
            };
            postToIframe(payload);
        },
        [postToIframe]
    );

    // request connect in iframe
    const postConnect = useCallback(
        ({ streamingUrl, sessionId, authToken }) => {
            postToIframe({
                type: "dcv:connect",
                payload: {
                    streamingUrl,
                    sessionId,
                    authToken,
                    baseUrl: `${window.location.origin}/dcvjs-umd`,
                    divId: "dcv-display",
                    enableWebCodecs: true,
                    enableBackgroundMode: true,
                    showToolbar: false,
                    toolbarAutoHide: true,
                },
            });
        },
        [postToIframe]
    );

    // request disconnect
    const postDisconnect = useCallback(() => {
        postToIframe({ type: "dcv:disconnect" });
    }, [postToIframe]);

    // request resolution change in iframe (width, height)
    const postRequestResolution = useCallback(
        (width, height) => {
            // Always round to integer pixels for DCV
            const w = Math.round(width);
            const h = Math.round(height);
            postToIframe({ type: "dcv:resize", width: w, height: h });
        },
        [postToIframe]
    );

    // ---- handle messages FROM iframe ----
    useEffect(() => {
        function onMessage(e) {
            const allowedFromIframe = dcvDisplayRef.current && e.source === dcvDisplayRef.current.contentWindow;
            const allowedSameOrigin = e.origin === window.location.origin;
            if (!allowedFromIframe && !allowedSameOrigin) return;

            const msg = e.data || {};
            switch (msg.type) {
                case "dcv:ready":
                    iframeReadyRef.current = true;
                    setDcvLoaded(true);
                    console.log("[useDCVStreaming] iframe ready");
                    break;
                case "dcv:connected":
                    dcvConnectionRef.current = { connected: true, meta: msg.meta || null };
                    setIsConnected(true);
                    setLoaderText("Connected. Waiting for first frame...");
                    console.log("[useDCVStreaming] iframe reports connected", msg);

                    if (!autoVacantEmitted) {
                        const tryEmitVacantSession = async (attempt = 1) => {
                            try {
                                const stored = localStorage.getItem("dcvSessionData");
                                const parsed = stored ? JSON.parse(stored) : null;
                                if (parsed?.userId === baseLaunchData.userId) {
                                    const containerUid = parsed?.data?.sessionDetails
                                        ? `${parsed.data.sessionDetails.instanceId}${parsed.data.sessionDetails.port}`
                                        : "";
                                    if (containerUid) {
                                        const payload = {
                                            message: "vacantSession",
                                            userId: baseLaunchData.userId,
                                            roomId,
                                            containerUid,
                                            avatarPreset,
                                            userDeviceType: /mobile/i.test(navigator.userAgent) ? "mobile" : "desktop",
                                            inviteSettings,
                                            clientTag,
                                        };
                                        sendSocketMessage("system_message", "", "", "", JSON.stringify(payload));
                                        console.log(
                                            `Vacant session payload emitted via socket (attempt ${attempt})`,
                                            payload
                                        );

                                        if (attempt < 4) {
                                            setTimeout(() => tryEmitVacantSession(attempt + 1), 3000);
                                        } else {
                                            setAutoVacantEmitted(true);
                                        }
                                    }
                                }
                            } catch (err) {
                                console.warn("Error emitting vacant session payload:", err);
                                if (attempt < 4) {
                                    setTimeout(() => tryEmitVacantSession(attempt + 1), 3000);
                                } else {
                                    setAutoVacantEmitted(true);
                                }
                            }
                        };
                        tryEmitVacantSession();

                        if (getFromSessionStorage("selectedAvatar")) {
                            sendSocketMessage("RPMAvatarEvent", "", baseLaunchData.userId, "LOAD_RPM_AVATAR", {
                                data: getFromSessionStorage("selectedAvatar"),
                            });
                        }
                    }
                    break;
                case "dcv:firstFrame":
                    (async () => {
                        try {
                            setFirstFrameReceived(true);
                            setLoaderText("Stream started");
                            console.log("[DCV] First frame received (iframe)");

                            // clear the first frame timeout if set
                            if (firstFrameTimeoutRef.current) {
                                clearTimeout(firstFrameTimeoutRef.current);
                                firstFrameTimeoutRef.current = null;
                            }
                            try {
                                // requestDisplayConfig(true)
                                postIframeRun("requestDisplayConfig", { immediate: true });

                                // setDisplayQuality(85, 100)
                                postIframeRun("setDisplayQuality", { quality: 85, maxQuality: 100 });

                                // enableDisplayQualityUpdates(true)
                                postIframeRun("enableDisplayQualityUpdates", { enabled: true });

                                // enableHighPixelDensity(true) if device + bandwidth look good
                                const isHiDPI = (window.devicePixelRatio || 1) >= 1.5;
                                const hasHighBandwidth = getStreamingQuality() === 2;
                                if (isHiDPI && hasHighBandwidth) {
                                    postIframeRun("enableHighPixelDensity", { enabled: true });
                                    console.log(
                                        "Requested iframe to enable high pixel density (HiDPI + high bandwidth)."
                                    );
                                }

                                // requestFullScreen
                                postIframeRun("requestFullScreen", {});

                                // requestFocus
                                postIframeRun("requestFocus", {});

                                // hide toolbar explicitly
                                postIframeRun("showToolbar", { visible: false });

                                // clipboard capture / sync
                                // we pass the id of the display container if present (fallback to 'dcv-display')
                                const displayId = dcvDisplayRef?.current?.id || "dcv-display";
                                postIframeRun("captureClipboardEvents", {
                                    enable: true,
                                    targetWindow: "parent",
                                    divId: displayId,
                                });
                                postIframeRun("syncClipboards", {});
                            } catch (err) {
                                console.warn("Error posting display tuning commands to iframe:", err);
                            }

                            // --- Vacant session emission ---
                            if (!autoVacantEmitted) {
                                try {
                                    const stored = localStorage.getItem("dcvSessionData");
                                    const parsed = stored ? JSON.parse(stored) : null;
                                    if (parsed?.userId === baseLaunchData.userId) {
                                        const containerUid = parsed?.data?.sessionDetails
                                            ? `${parsed.data.sessionDetails.instanceId}${parsed.data.sessionDetails.port}`
                                            : "";
                                        if (containerUid) {
                                            const payload = {
                                                message: "vacantSession",
                                                userId: baseLaunchData.userId,
                                                roomId,
                                                containerUid,
                                                avatarPreset,
                                                userDeviceType: /mobile/i.test(navigator.userAgent)
                                                    ? "mobile"
                                                    : "desktop",
                                                inviteSettings,
                                            };

                                            sendSocketMessage("system_message", "", "", "", JSON.stringify(payload));
                                            setAutoVacantEmitted(true);
                                            console.log("Vacant session payload emitted via socket", payload);
                                        }
                                    }
                                } catch (err) {
                                    console.warn("Error emitting vacant session payload:", err);
                                }
                            }

                            // Ask iframe to request the resolution that fits current window
                            try {
                                postRequestResolution(window.innerWidth, window.innerHeight);
                                console.log(
                                    `Requested DCV resolution via iframe: ${window.innerWidth}×${window.innerHeight}`
                                );
                            } catch (err) {
                                console.error("Error requesting resolution from iframe:", err);
                            }

                            if (typeof window.completeProgress === "function") window.completeProgress();

                            // finally apply device selection (this posts dcv:setDevices to iframe)
                            await enableDevices();
                        } catch (err) {
                            console.error("Error in parent firstFrame handler:", err);
                        }
                    })();
                    break;
                case "dcv:disconnected":
                    setIsConnected(false);
                    dcvConnectionRef.current = null;
                    // Only redirect to home if this is NOT an intentional disconnect (e.g., during retry)
                    if (isIntentionalDisconnectRef.current) {
                        console.log("[useDCVStreaming] iframe reports disconnected (intentional, skipping redirect)");
                        isIntentionalDisconnectRef.current = false; // Reset the flag
                    } else {
                        handleDcvDisconnect();
                        console.log("[useDCVStreaming] iframe reports disconnected");
                    }
                    break;
                case "dcv:authError":
                    console.error("[useDCVStreaming] authError:", msg.error);
                    // Use ref to access the auth error handler (defined later in the hook)
                    if (handleAuthErrorRef.current) {
                        handleAuthErrorRef.current(msg.error);
                    } else {
                        // Fallback if ref not yet set
                        setLoaderText("Authentication error");
                    }
                    break;
                case "dcv:gestureRequired":
                    console.warn("[useDCVStreaming] iframe says user gesture required for audio/autoplay");
                    setLoaderText("User gesture required for audio");
                    break;
                case "dcv:log":
                    // debugging channel
                    console.debug("[iframe dcv]", msg.message);
                    break;
                default:
                    break;
            }
        }

        window.addEventListener("message", onMessage);
        return () => window.removeEventListener("message", onMessage);
    }, []);

    // ---- enableDevices: posts to iframe to set mic/cam/audioOutput ----
    const enableDevices = useCallback(async () => {
        // if no iframe ready, skip
        if (!iframeReadyRef.current) {
            console.warn("enableDevices: iframe not ready yet");
            return;
        }

        try {
            postSetDevices(selectedDevices);
        } catch (err) {
            console.error("Error enabling devices (iframe):", err);
        }
    }, [postSetDevices, selectedDevices]);

    // Only apply device selection after first frame is received and when devices change
    useEffect(() => {
        if (firstFrameReceived) {
            enableDevices();
        }
    }, [firstFrameReceived, selectedDevices]);

    // ---- resize handling----
    useEffect(() => {
        const handleResize = () => {
            const width = Math.max(1, window.innerWidth);
            const height = Math.max(1, window.innerHeight);
            if (iframeReadyRef.current) {
                postRequestResolution(width, height);
            }
        };
        window.addEventListener("resize", handleResize);
        // call on mount
        handleResize();
        return () => window.removeEventListener("resize", handleResize);
    }, [postRequestResolution]);

    // When you obtain streamingUrl from polling, call postConnect(streamingUrl)
    const connect = useCallback(
        (sessionId, authToken, serverUrl) => {
            // This function will still be called by your auth-success path
            // but we will post the streamingUrl to the iframe (serverUrl here is actually the streamingUrl in your flow)
            const streamingUrlToUse = serverUrl || streamingUrl;
            if (!streamingUrlToUse) {
                console.error("connect: missing streamingUrl");
                return;
            }

            // reset first frame state & start timeout just like before
            setFirstFrameReceived(false);
            setLoaderText("Connecting to streaming server...");
            if (firstFrameTimeoutRef.current) {
                clearTimeout(firstFrameTimeoutRef.current);
            }
            firstFrameTimeoutRef.current = setTimeout(() => {
                // timed out waiting for first frame -> disconnect and re-init
                postDisconnect();
                setLoaderText("Timed out waiting for first frame, retrying...");
                initialiseRenderStreamingSession().catch(console.error);
            }, FIRST_FRAME_TIMEOUT);

            // Post connect to iframe with all required fields
            postConnect({ streamingUrl: streamingUrlToUse, sessionId, authToken });
            console.log("[useDCVStreaming] posted connect to iframe:", streamingUrlToUse, sessionId, authToken);
        },
        [postConnect, postDisconnect, streamingUrl]
    );

    // ---- beginPollingSessionStatus, requestStreamingSession, initialiseRenderStreamingSession
    const beginPollingSessionStatus = useCallback(
        async (sessionId) => {
            if (activePollRef.current) return activePollRef.current;

            // Reset elapsed seconds and start timer
            setPollingElapsedSeconds(0);
            if (pollingTimerRef.current) clearInterval(pollingTimerRef.current);
            pollingTimerRef.current = setInterval(() => {
                setPollingElapsedSeconds((prev) => prev + 1);
            }, 1000);

            activePollRef.current = new Promise((resolve) => {
                const POLL_INTERVAL_MS = 15000;
                const MAX_DURATION_MS = 15 * 60 * 1000;
                const apiEndpoint = `${baseLaunchData.coreApiUri}/web/dcv/${sessionId}`;

                const timeoutId = setTimeout(() => {
                    clearInterval(intervalId);
                    clearInterval(pollingTimerRef.current);
                    pollingTimerRef.current = null;
                    setPollingElapsedSeconds(0);
                    activePollRef.current = null;
                    resolve(null);
                }, MAX_DURATION_MS);

                const intervalId = setInterval(async () => {
                    try {
                        const response = await fetch(apiEndpoint, {
                            headers: {
                                "Content-Type": "application/json",
                                Authorization: `Bearer ${baseLaunchData.token}`,
                            },
                        });
                        const result = await response.json();
                        const streamingUrl = result.data?.sessionDetails?.streamingUrl;
                        setStreamingUrl(streamingUrl);
                        console.log("[POLL] Session status:", result.data?.status);
                        setLoaderText(`Polling session status... ${result.data?.status || "pending"}`);
                        if (result.data?.status === "occupied" && streamingUrl) {
                            const dataToStore = { userId: baseLaunchData.userId, data: result.data };
                            localStorage.setItem("dcvSessionData", JSON.stringify(dataToStore));
                            console.log("Session is now occupied, proceeding to connect...", result.data);
                            setLoaderText("Session is now occupied, proceeding to connect...");
                            clearTimeout(timeoutId);
                            clearInterval(intervalId);
                            clearInterval(pollingTimerRef.current);
                            pollingTimerRef.current = null;
                            setPollingElapsedSeconds(0);
                            activePollRef.current = null;
                            resolve(result.data);
                        }
                    } catch (err) {
                        console.error("[POLL] Error:", err);
                    }
                }, POLL_INTERVAL_MS);
            });

            return activePollRef.current;
        },
        [baseLaunchData.coreApiUri, baseLaunchData.token, baseLaunchData.userId]
    );

    const requestStreamingSession = useCallback(
        async (payload) => {
            const apiEndpoint = `${baseLaunchData.coreApiUri}/web/dcv/${payload.userId}`;
            const response = await fetch(apiEndpoint, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${payload.token}`,
                },
                body: JSON.stringify(payload),
            });

            const result = await response.json();
            if (!response.ok) throw new Error(`Session request failed: ${response.status}`);
            setLoaderText("Session created, waiting for readiness...");
            return result?.data?.sessionId;
        },
        [baseLaunchData.coreApiUri]
    );
    const initialiseRenderStreamingSession = useCallback(async () => {
        console.log("Initialising render streaming session (iframe-based)...");
        setLoaderText("Initializing streaming session...");

        if (dcvConnectionRef.current && firstFrameReceived) return;

        try {
            // Load session data from localStorage
            let sessionData = localStorage.getItem("dcvSessionData");
            sessionData = sessionData ? JSON.parse(sessionData) : null;

            let responseValid = false;
            if (sessionData?.data?.sessionId !== undefined) {
                setLoaderText("Checking session availability...");
                responseValid = await isSessionValidAndActive(
                    `${baseLaunchData.coreApiUri}/web/dcv/${sessionData.data.sessionId}`,
                    baseLaunchData.token
                );
            }

            // Invalidate session if no longer valid or userId mismatch
            if ((!responseValid && sessionData) || (sessionData && sessionData.userId !== baseLaunchData.userId)) {
                console.log("Stored session data is invalid or userId mismatch, discarding.");
                sessionData = null;
            }

            if (responseValid && sessionData) {
                setLoaderText("Using existing session...");
            }

            let streamingUrlLocal = sessionData?.data?.sessionDetails?.streamingUrl;

            // Create new session if needed
            if (!sessionData) {
                console.log("No valid session data, creating new session.");
                const selectedRegion = await determineNearestRegion();
                setLoaderText("Selecting nearest region...");
                const payload = {
                    userId: baseLaunchData.userId,
                    token: baseLaunchData.token,
                    region: selectedRegion,
                };
                const sessionId = await requestStreamingSession(payload);
                sessionData = await beginPollingSessionStatus(sessionId);
                streamingUrlLocal = sessionData?.sessionDetails?.streamingUrl;
            }

            console.log("Final session data:", sessionData);

            if (!sessionData || !streamingUrlLocal) {
                console.error("No valid session data or streaming URL available, cannot proceed.");
                return;
            }

            // Wait for iframe to be ready before posting connect
            if (!iframeReadyRef.current) {
                setLoaderText("Waiting for player to be ready...");
                const deadline = Date.now() + 10000; // 10s timeout
                while (!iframeReadyRef.current && Date.now() < deadline) {
                    // eslint-disable-next-line no-await-in-loop
                    await new Promise((r) => setTimeout(r, 200));
                }
            }

            if (!iframeReadyRef.current) {
                console.error("Iframe did not become ready within timeout, cannot connect.");
                return;
            }

            setLoaderText("Authenticating with streaming server...");
            console.log("Will post connect to iframe; iframeReady:", iframeReadyRef.current);
            postConnect({
                streamingUrl: streamingUrlLocal,
                sessionId: sessionData?.data?.sessionId,
                authToken: sessionData?.data?.authToken,
            });
            console.log("iFrame loaded: will trigger iframe timer", dcvLoaded);
            if (dcvDisplayRef.current && dcvDisplayRef.current.contentWindow) {
                dcvDisplayRef.current.contentWindow.postMessage({ type: "dcv:authenticating" }, window.location.origin);
            }
        } catch (err) {
            console.error("initialiseRenderStreamingSession error:", err);
        }
    }, [beginPollingSessionStatus, requestStreamingSession, postConnect, baseLaunchData, firstFrameReceived]);

    // --- Flush session data and retry with a fresh session ---
    const flushSessionAndRetry = useCallback(
        async (reason = "manual") => {
            console.log(`[useDCVStreaming] flushSessionAndRetry called. Reason: ${reason}`);

            // Clear any pending retry timeout
            if (authRetryTimeoutRef.current) {
                clearTimeout(authRetryTimeoutRef.current);
                authRetryTimeoutRef.current = null;
            }

            // Clear stored session data
            try {
                localStorage.removeItem("dcvSessionData");
                console.log("[useDCVStreaming] Cleared dcvSessionData from localStorage");
            } catch (e) {
                console.warn("[useDCVStreaming] Error clearing localStorage:", e);
            }

            // Disconnect current iframe connection if any
            // Mark as intentional so we don't redirect to home on dcv:disconnected
            try {
                isIntentionalDisconnectRef.current = true;
                postDisconnect();
            } catch (e) {
                console.warn("[useDCVStreaming] Error disconnecting:", e);
                isIntentionalDisconnectRef.current = false;
            }

            // Reset connection state
            setIsConnected(false);
            setFirstFrameReceived(false);
            dcvConnectionRef.current = null;
            setStreamingUrl("");
            setAutoVacantEmitted(false);

            // Reset error state if this is a fresh retry
            if (reason === "manual") {
                setAuthError(null);
                setAuthRetryCount(0);
            }

            setIsRetrying(true);
            setLoaderText("Restarting session...");

            // Small delay to allow iframe state to reset
            await new Promise((resolve) => setTimeout(resolve, 500));

            // Wait for iframe to be ready
            if (!iframeReadyRef.current) {
                setLoaderText("Waiting for player to be ready...");
                const deadline = Date.now() + 10000;
                while (!iframeReadyRef.current && Date.now() < deadline) {
                    // eslint-disable-next-line no-await-in-loop
                    await new Promise((r) => setTimeout(r, 200));
                }
            }

            setIsRetrying(false);

            // Initialize new session
            try {
                await initialiseRenderStreamingSession();
            } catch (err) {
                console.error("[useDCVStreaming] Error during retry:", err);
                setLoaderText("Retry failed. Please try again.");
            }
        },
        [postDisconnect, initialiseRenderStreamingSession]
    );

    // --- Auto-retry on auth error with exponential backoff ---
    const handleAuthErrorWithRetry = useCallback(
        (errorDetails) => {
            const errorMessage =
                typeof errorDetails === "string"
                    ? errorDetails
                    : errorDetails?.message || errorDetails?.error || JSON.stringify(errorDetails);

            console.error("[useDCVStreaming] Authentication error:", errorMessage);
            setAuthError({ message: errorMessage, timestamp: Date.now(), details: errorDetails });

            const currentRetry = authRetryCount;

            if (currentRetry < MAX_AUTH_RETRIES) {
                const nextRetry = currentRetry + 1;
                const delayMs = AUTH_RETRY_BASE_DELAY_MS * 2 ** currentRetry; // Exponential backoff: 2s, 4s, 8s

                setAuthRetryCount(nextRetry);
                setLoaderText(
                    `Authentication failed. Retrying (${nextRetry}/${MAX_AUTH_RETRIES}) in ${delayMs / 1000}s...`
                );

                console.log(`[useDCVStreaming] Scheduling auth retry ${nextRetry}/${MAX_AUTH_RETRIES} in ${delayMs}ms`);

                authRetryTimeoutRef.current = setTimeout(() => {
                    flushSessionAndRetry("auth_retry");
                }, delayMs);
            } else {
                setLoaderText("Authentication failed. Click to retry.");
                console.error(`[useDCVStreaming] Max auth retries (${MAX_AUTH_RETRIES}) exceeded.`);
            }
        },
        [authRetryCount, flushSessionAndRetry]
    );

    // Keep ref in sync with handleAuthErrorWithRetry for useEffect access
    useEffect(() => {
        handleAuthErrorRef.current = handleAuthErrorWithRetry;
    }, [handleAuthErrorWithRetry]);

    // --- DEBUG: Simulate auth error for testing retry flow ---
    const debugSimulateAuthError = useCallback(() => {
        console.warn("[DEBUG] Simulating auth error to test retry flow");
        handleAuthErrorWithRetry({
            code: "DEBUG_SIMULATED",
            message: "Simulated auth error for testing",
            timestamp: new Date().toISOString(),
        });
    }, [handleAuthErrorWithRetry]);

    // --- Join button handler (keeps same semantics)
    const onJoinButtonClicked = useCallback(() => {
        if (!dcvDisplayRef.current) {
            // wait for iframe element to be present in DOM (parent component should render the iframe)
            const retry = setInterval(() => {
                if (dcvDisplayRef.current) {
                    clearInterval(retry);
                    if (!isConnected) initialiseRenderStreamingSession().catch(console.error);
                }
            }, 100);
            return;
        }

        if (isConnected === false) {
            initialiseRenderStreamingSession().catch(console.error);
        }
    }, [isConnected, initialiseRenderStreamingSession]);

    // cleanup on unmount
    useEffect(() => {
        return () => {
            if (firstFrameTimeoutRef.current) {
                clearTimeout(firstFrameTimeoutRef.current);
                firstFrameTimeoutRef.current = null;
            }
            if (authRetryTimeoutRef.current) {
                clearTimeout(authRetryTimeoutRef.current);
                authRetryTimeoutRef.current = null;
            }
            activePollRef.current = null;
            if (pollingTimerRef.current) {
                clearInterval(pollingTimerRef.current);
                pollingTimerRef.current = null;
            }
            setPollingElapsedSeconds(0);
            try {
                postDisconnect();
            } catch {
                // Ignore errors
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Return same shape as before; dcvDisplayRef now expects you to render an iframe with ref={dcvDisplayRef} and src="/dcv-iframe.html"
    return {
        dcvDisplayRef,
        onJoinButtonClicked,
        isConnected,
        firstFrameReceived,
        enableDevices,
        dcvConnectionRef,
        loaderText,
        streamingUrl,
        pollingElapsedSeconds,
        // Auth error state & retry functionality
        authError,
        authRetryCount,
        isRetrying,
        flushSessionAndRetry,
        maxAuthRetries: MAX_AUTH_RETRIES,
        // extras:
        postRequestResolution,
        postConnect,
        postDisconnect,
        iframeReady: iframeReadyRef.current,
        // Debug utilities (remove in production if desired)
        debugSimulateAuthError,
    };
};
